/** Console v0.5: the shared components (design brief v0.5 s.4), built
 * once and used by every screen. Classes only -- the console's CSP has no
 * 'unsafe-inline', so nothing here sets a style attribute; sizes and
 * colours live in theme.css (the .pk5-* rules), from the --pk-* tokens. */

import { statusClass, tabKeyTarget } from "./routes.js";

type Child = Node | string | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  for (const c of children) if (c !== null && c !== undefined && c !== false) node.append(c);
  return node;
}

let seq = 0;
/** A unique id for aria wiring (no two dialogs or tab sets share one). */
export function uid(prefix: string): string {
  seq += 1;
  return `${prefix}-${seq}`;
}

export type ButtonVariant = "primary" | "secondary" | "destructive" | "quiet" | "accent";

export function button(label: string | Node, variant: ButtonVariant, onClick?: (ev: MouseEvent) => void): HTMLButtonElement {
  const b = h("button", `pk5-btn pk5-btn-${variant}`, label);
  b.type = "button";
  if (onClick) b.addEventListener("click", onClick);
  return b;
}

/** A link styled as a button (navigation, not an action). */
export function linkButton(label: string | Node, href: string, variant: ButtonVariant): HTMLAnchorElement {
  const a = h("a", `pk5-btn pk5-btn-${variant}`, label);
  a.href = href;
  return a;
}

/** Every figure, date, ABN, code and count: DM Mono, tabular (brief s.4). */
export function figure(text: string): HTMLSpanElement {
  return h("span", "pk5-figure", text);
}

/** A status pill with its label always visible (PRODUCT-UI.md s.3). */
export function chip(status: string): HTMLSpanElement {
  return h("span", `pk5-chip ${statusClass(status)}`, status);
}

/** A small neutral tag ("On", "Coming soon", a service name). Not a status. */
export function tag(text: string, tone: "neutral" | "good" | "warn" = "neutral"): HTMLSpanElement {
  return h("span", `pk5-tag pk5-tag-${tone}`, text);
}

export function card(cls?: string, ...children: Child[]): HTMLElement {
  return h("section", `pk5-card${cls ? ` ${cls}` : ""}`, ...children);
}

/** A card with an 18/700 heading (and an optional link or action on the right), labelled by that heading. */
export function sectionCard(title: string, children: Child[], aside?: Node): HTMLElement {
  const id = uid("pk5-h");
  const heading = h("h2", "pk5-section-title", title);
  heading.id = id;
  const head = h("div", "pk5-card-head", heading, aside ?? null);
  const section = card(undefined, head, ...children);
  section.setAttribute("aria-labelledby", id);
  return section;
}

/** The title block every page starts with: h1 28/700, a 15 px intro, actions on the right. */
export function pageHead(title: string, intro: string | null, actions: Node[] = []): HTMLElement {
  const h1 = h("h1", "pk5-title", title);
  h1.id = "pk5-title";
  h1.tabIndex = -1;
  const text = h("div", "pk5-head-text", h1, intro ? h("p", "pk5-intro", intro) : null);
  return h("div", "pk5-page-head", text, actions.length ? h("div", "pk5-head-actions", ...actions) : null);
}

export function statTile(label: string, value: string, sub?: Child, href?: string): HTMLElement {
  const inner: Child[] = [h("span", "pk5-stat-label", label), h("span", "pk5-stat-value pk5-figure", value), sub ? h("span", "pk5-stat-sub", sub) : null];
  if (href) {
    const a = h("a", "pk5-stat", ...inner);
    a.href = href;
    return a;
  }
  return h("div", "pk5-stat", ...inner);
}

/** A progress bar with its figure beside it; the bar itself is decoration (the figure says it). */
export function progress(done: number, total: number, label: string): HTMLElement {
  const pct = total > 0 ? Math.max(0, Math.min(100, Math.round((done / total) * 100))) : 0;
  const bar = h("span", "pk5-progress-bar");
  bar.dataset.pct = String(pct);
  // CSSOM, not a style attribute: allowed under the console's CSP.
  bar.style.setProperty("--pk5-pct", `${pct}%`);
  const track = h("span", "pk5-progress-track", bar);
  track.setAttribute("aria-hidden", "true");
  const wrap = h("div", "pk5-progress", h("span", "pk5-progress-label", label), track, figure(`${done}/${total}`));
  return wrap;
}

export function emptyState(title: string, body?: string, action?: Node): HTMLElement {
  return h("div", "pk5-empty", h("p", "pk5-empty-title", title), body ? h("p", "pk5-empty-body", body) : null, action ?? null);
}

export function notice(text: string | Node, kind: "status" | "alert" = "status"): HTMLElement {
  const n = h("div", kind === "alert" ? "pk5-notice pk5-notice-alert" : "pk5-notice", text);
  n.setAttribute("role", kind);
  return n;
}

export function loading(): HTMLElement {
  const n = h("div", "pk5-loading", "Loading…");
  n.setAttribute("role", "status");
  return n;
}

export interface TabDef<T extends string> {
  id: T;
  label: string;
  count?: number | null;
}

/** Tabs (48 px, 2 px ink underline): role=tablist, arrow keys / Home / End move and select. */
export function tabs<T extends string>(label: string, defs: ReadonlyArray<TabDef<T>>, selected: T, onSelect: (id: T) => void, panelId?: string): HTMLElement {
  const list = h("div", "pk5-tabs");
  list.setAttribute("role", "tablist");
  list.setAttribute("aria-label", label);
  const buttons: HTMLButtonElement[] = [];
  defs.forEach((d, i) => {
    const on = d.id === selected;
    const b = h("button", `pk5-tab${on ? " pk5-tab-on" : ""}`, d.label, d.count !== undefined && d.count !== null ? h("span", "pk5-tab-count pk5-figure", String(d.count)) : null);
    b.type = "button";
    b.setAttribute("role", "tab");
    b.setAttribute("aria-selected", on ? "true" : "false");
    b.tabIndex = on ? 0 : -1;
    if (panelId) b.setAttribute("aria-controls", panelId);
    b.addEventListener("click", () => onSelect(d.id));
    b.addEventListener("keydown", (ev) => {
      const to = tabKeyTarget(ev.key, i, defs.length);
      if (to === null) return;
      ev.preventDefault();
      onSelect(defs[to]!.id);
      // The tabs are redrawn by onSelect; focus the new one once they are.
      queueMicrotask(() => {
        const again = document.querySelectorAll<HTMLButtonElement>(`[aria-label="${CSS.escape(label)}"] [role="tab"]`);
        (again[to] ?? buttons[to])?.focus();
      });
    });
    buttons.push(b);
    list.append(b);
  });
  return list;
}

/** A table inside its card that scrolls sideways on a phone (brief s.3), with a hidden caption. */
export function table(caption: string, columns: ReadonlyArray<{ label: string; num?: boolean }>, rows: ReadonlyArray<ReadonlyArray<Child>>): HTMLElement {
  const t = h("table", "pk5-table");
  t.append(h("caption", "pk-visually-hidden", caption));
  const tr = h("tr");
  for (const c of columns) {
    const th = h("th", c.num ? "pk5-num" : null, c.label);
    th.scope = "col";
    tr.append(th);
  }
  t.append(h("thead", null, tr));
  const body = h("tbody");
  for (const r of rows) {
    const row = h("tr");
    r.forEach((cell, i) => row.append(h("td", columns[i]?.num ? "pk5-num" : null, cell)));
    body.append(row);
  }
  t.append(body);
  const wrap = h("div", "pk5-table-wrap", t);
  wrap.tabIndex = 0;
  wrap.setAttribute("role", "region");
  wrap.setAttribute("aria-label", caption);
  return wrap;
}

/** A labelled switch row (Approval rules, Notifications). */
export function toggleRow(label: string, hint: string, checked: boolean, onChange: ((on: boolean) => void) | null): HTMLElement {
  const input = h("input", "pk5-switch");
  input.type = "checkbox";
  input.checked = checked;
  input.setAttribute("role", "switch");
  if (onChange) input.addEventListener("change", () => onChange(input.checked));
  else input.disabled = true;
  return h("label", "pk5-toggle-row", h("span", "pk5-toggle-text", h("span", "pk5-toggle-label", label), h("span", "pk5-toggle-hint", hint)), input);
}

export interface DialogAction {
  label: string;
  variant: ButtonVariant;
  /** Return false (or a promise of false) to keep the dialog open, e.g. on an error. */
  onClick: (close: () => void, btn: HTMLButtonElement) => void | boolean | Promise<void | boolean>;
  /** The safe choice: it has focus when the dialog opens, and Escape picks it. */
  safe?: boolean;
}

/** A modal dialog (native <dialog>, showModal: focus stays inside). Unique ids; focus returns to
 * whatever opened it when it closes. */
export function openDialog(title: string, body: Child[], actions: DialogAction[], opts: { wide?: boolean } = {}): HTMLDialogElement {
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const dialog = h("dialog", `pk5-dialog${opts.wide ? " pk5-dialog-wide" : ""}`);
  const titleId = uid("pk5-dialog-title");
  const bodyId = uid("pk5-dialog-body");
  const heading = h("h2", "pk5-dialog-title", title);
  heading.id = titleId;
  const content = h("div", "pk5-dialog-body", ...body);
  content.id = bodyId;
  dialog.setAttribute("aria-labelledby", titleId);
  dialog.setAttribute("aria-describedby", bodyId);
  const close = () => dialog.close();
  const row = h("div", "pk5-dialog-actions");
  let safe: HTMLButtonElement | null = null;
  for (const a of actions) {
    const b = button(a.label, a.variant);
    b.addEventListener("click", () => void a.onClick(close, b));
    if (a.safe) safe = b;
    row.append(b);
  }
  dialog.append(heading, content, row);
  dialog.addEventListener("close", () => {
    dialog.remove();
    if (opener && opener.isConnected) opener.focus();
  });
  document.body.append(dialog);
  dialog.showModal();
  (safe ?? row.querySelector("button"))?.focus();
  return dialog;
}

/** A "⋯ More actions" style menu button: role=menu, arrow keys, Escape closes and returns focus. */
export function menuButton(label: string, items: ReadonlyArray<{ label: string; onSelect: () => void; destructive?: boolean } | "divider">, text?: string): HTMLElement {
  const wrap = h("div", "pk5-menu-wrap");
  const trigger = button(text ?? "⋯", "secondary");
  trigger.classList.add("pk5-menu-trigger");
  trigger.setAttribute("aria-label", label);
  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", "false");
  const menu = h("div", "pk5-menu");
  menu.setAttribute("role", "menu");
  menu.hidden = true;
  const menuId = uid("pk5-menu");
  menu.id = menuId;
  trigger.setAttribute("aria-controls", menuId);
  const entries: HTMLButtonElement[] = [];
  const shut = (focusTrigger: boolean) => {
    menu.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    if (focusTrigger) trigger.focus();
  };
  for (const item of items) {
    if (item === "divider") {
      const d = h("div", "pk5-menu-divider");
      d.setAttribute("role", "separator");
      menu.append(d);
      continue;
    }
    const b = h("button", `pk5-menu-item${item.destructive ? " pk5-menu-item-destructive" : ""}`, item.label);
    b.type = "button";
    b.setAttribute("role", "menuitem");
    b.tabIndex = -1;
    b.addEventListener("click", () => {
      shut(false);
      item.onSelect();
    });
    entries.push(b);
    menu.append(b);
  }
  trigger.addEventListener("click", () => {
    const opening = menu.hidden;
    menu.hidden = !opening;
    trigger.setAttribute("aria-expanded", opening ? "true" : "false");
    if (opening) entries[0]?.focus();
  });
  menu.addEventListener("keydown", (ev) => {
    const i = entries.indexOf(document.activeElement as HTMLButtonElement);
    if (ev.key === "Escape") {
      ev.preventDefault();
      shut(true);
    } else if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      ev.preventDefault();
      const n = entries.length;
      entries[(i + (ev.key === "ArrowDown" ? 1 : n - 1)) % n]?.focus();
    } else if (ev.key === "Tab") {
      shut(false);
    }
  });
  document.addEventListener("click", (ev) => {
    if (!menu.hidden && !wrap.contains(ev.target as Node)) shut(false);
  });
  wrap.append(trigger, menu);
  return wrap;
}

/** A list + detail split pane (Review inbox, Meetings): the list is a set of pressed-state buttons. */
export function splitPane(listLabel: string, list: Node[], detail: Node): HTMLElement {
  const l = h("div", "pk5-split-list", ...list);
  l.setAttribute("role", "region");
  l.setAttribute("aria-label", listLabel);
  return h("div", "pk5-split", l, h("div", "pk5-split-detail", detail));
}

export function listRow(title: Child, meta: Child, right: Child, selected: boolean, onPick: () => void, extra?: Child): HTMLButtonElement {
  const b = h(
    "button",
    `pk5-row${selected ? " pk5-row-on" : ""}`,
    h("span", "pk5-row-line", h("span", "pk5-row-title", title), h("span", "pk5-row-right", right)),
    h("span", "pk5-row-line", h("span", "pk5-row-meta", meta), h("span", "pk5-row-extra", extra ?? null))
  );
  b.type = "button";
  b.setAttribute("aria-pressed", selected ? "true" : "false");
  b.addEventListener("click", onPick);
  return b;
}

/** Initials in a rounded square (an organisation or person, beside the name). */
export function avatar(text: string): HTMLSpanElement {
  const a = h("span", "pk5-avatar", text);
  a.setAttribute("aria-hidden", "true");
  return a;
}

const ICONS = {
  bell: "M10 3a5 5 0 0 0-5 5v3.5L3.5 14h13L15 11.5V8a5 5 0 0 0-5-5zM8 16.5a2 2 0 0 0 4 0",
  search: "M9 4a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM13 13l4 4",
  chevron: "M6 8l4 4 4-4",
} as const;

/** A line icon (20 px grid, currentColor), decorative: the control around it carries the name. */
export function icon(name: keyof typeof ICONS, size = 18): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.6");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS(NS, "path");
  path.setAttribute("d", ICONS[name]);
  svg.append(path);
  return svg;
}

export const LEGAL_FOOTER = "Preekie is a product of CPAI Australia Pty Ltd · ACN 674 037 668";

export function legalFooter(): HTMLElement {
  return h("footer", "pk5-footer", LEGAL_FOOTER);
}
